import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { EventCard } from "@/components/EventCard";
import { RotatingBookNow } from "@/components/RotatingBookNow";
import { Spinner } from "@/components/common";

const HERO_BG = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&q=75&w=1600";

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [past, setPast] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/events/featured"),
      api.get("/events", { params: { status: "upcoming" } }),
      api.get("/events", { params: { status: "coming_soon" } }),
      api.get("/events", { params: { status: "past" } }),
      api.get("/settings"),
    ]).then(([f, u, c, p, s]) => {
      setFeatured(f.data);
      setUpcoming(u.data);
      setComingSoon(c.data);
      setPast(p.data.slice(0, 3));
      setSettings(s.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  const poster = featured?.poster ? fileUrl(featured.poster) : HERO_BG;
  const minPrice = featured ? (featured.ticket_categories || []).reduce((m, c) => (c.price < m ? c.price : m), Infinity) : Infinity;

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative flex min-h-[85vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={poster} alt="" className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-[#0A0A0C]/40" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-8">
          <div className="fade-up max-w-3xl">
            <p className="caption">MUSIC • PEOPLE • MOMENTS • TOGETHER</p>
            <h1 className="mt-4 font-serif text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              <span className="gold-text">SIRAKH</span><br />CREATIONS
            </h1>
            <p className="mt-5 max-w-xl text-lg text-zinc-300" data-testid="home-title">
              {settings?.home_title || "Premium live experiences. Legendary artists. Unforgettable nights worth keeping forever."}
            </p>

            {featured ? (
              <div className="mt-10 card-dark max-w-xl p-6 sm:p-7" data-testid="featured-event">
                <p className="caption">Featured Event</p>
                <h2 className="mt-2 font-serif text-2xl font-bold sm:text-3xl">{featured.name}</h2>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
                  {featured.date && <span className="flex items-center gap-2"><Calendar size={15} className="text-[#D4AF37]" />{featured.date}</span>}
                  {featured.time && <span className="flex items-center gap-2"><Clock size={15} className="text-[#D4AF37]" />{featured.time}</span>}
                  {featured.venue && <span className="flex items-center gap-2"><MapPin size={15} className="text-[#D4AF37]" />{featured.venue}</span>}
                </div>
                {minPrice !== Infinity && (
                  <p className="mt-4 text-sm text-zinc-400">Starting from <span className="font-serif text-xl font-bold text-[#E5C158]">₹{minPrice}</span></p>
                )}
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link to={`/events/${featured.id}?book=1`} className="btn-gold flex-1" data-testid="book-tickets-hero-button">BOOK TICKETS</Link>
                  <Link to={`/events/${featured.id}`} className="btn-outline flex-1" data-testid="view-event-hero-button">VIEW EVENT</Link>
                </div>
              </div>
            ) : (
              <div className="mt-10">
                <Link to="/events" className="btn-gold" data-testid="browse-events-hero">BROWSE EVENTS <ArrowRight size={18} /></Link>
              </div>
            )}
          </div>
        </div>
      </section>

      <Section title="Upcoming Events" items={upcoming} link="/events" linkLabel="All events"
        empty="New events will be announced soon." testid="home-upcoming" />
      {comingSoon.length > 0 && <Section title="Coming Soon" items={comingSoon} testid="home-coming-soon" />}
      {past.length > 0 && <Section title="Past Events" items={past} link="/past-events" linkLabel="View all past events" testid="home-past" />}

      <RotatingBookNow settings={settings} />
    </div>
  );
}

const Section = ({ title, items, link, linkLabel, empty, testid }) => (
  <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8" data-testid={testid}>
    <div className="mb-8 flex items-end justify-between">
      <h2 className="font-serif text-2xl font-bold sm:text-3xl">{title}</h2>
      {link && <Link to={link} className="flex items-center gap-1 text-sm text-[#E5C158] hover:underline">{linkLabel} <ArrowRight size={14} /></Link>}
    </div>
    {items.length ? (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((e) => <EventCard key={e.id} event={e} />)}
      </div>
    ) : empty ? <p className="text-sm text-zinc-500">{empty}</p> : null}
  </section>
);
